import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Skeleton } from "@/components/ui/skeleton.tsx";
import { BreakdownBarChartCard } from "@/components/BreakdownBarChartCard.tsx";
import { DataTable } from "@/components/data-table/data-table.tsx";
import { PageTitle } from "@/components/ui/headings";
import { ToolbarOptions } from "@/types/data-table/ToolbarOptions.ts";
import { queryKeys } from "@/services/queryKeyFactory.ts";
import {
    getMentees,
    getMenteeBreakdownStats,
    type MenteeBreakdownStats,
} from "@/services/menteeService.ts";
import { getEffectiveDeadlines } from "@/services/deadlineExtensionService.ts";
import { useTranslation } from "react-i18next";
import { getMenteesColumns } from "@/pages/mentees/mentees-columns.tsx";

const MenteesPage = () => {
    const { t } = useTranslation();
    const { data: mentees, isFetching } = useQuery({
        queryKey: queryKeys.fetchMentees.all,
        queryFn: () => getMentees(),
        initialData: [],
    });

    const { data: breakdown, isFetching: breakdownFetching } = useQuery<MenteeBreakdownStats>({
        queryKey: queryKeys.fetchMentees.breakdown,
        queryFn: () => getMenteeBreakdownStats(),
    });

    const { data: effectiveDeadlines } = useQuery({
        queryKey: queryKeys.effectiveDeadlines,
        queryFn: getEffectiveDeadlines,
    });

    const mentorSelectionDeadline = effectiveDeadlines?.mentorSelection
        ? new Date(effectiveDeadlines.mentorSelection)
        : null;
    const isBeforeMentorSelectionDeadline = useMemo(() => {
        if (!mentorSelectionDeadline) return true;
        const now = new Date();
        const endOfDeadlineDay = new Date(mentorSelectionDeadline);
        endOfDeadlineDay.setHours(23, 59, 59, 999);
        return now <= endOfDeadlineDay;
    }, [mentorSelectionDeadline]);

    const toolbarOptions: ToolbarOptions = {
        filters: [
            { column: "submittedPreference", title: "submittedPreference" },
            { column: "selectedMentor", title: "selectedMentor" },
            { column: "studentStatus", title: "studentStatus" },
            { column: "disciplines", title: "disciplines" },
            { column: "institution", title: "institution" },
            { column: "region", title: "region" },
        ],
        searchableColumn: "name",
        sendEmailColumn: "email",
    };

    const hiddenColumns: string[] = ["submittedPreference", "selectedMentor"];

    const columns = useMemo(
        () =>
            getMenteesColumns({
                showPreferenceBeforeDeadline: isBeforeMentorSelectionDeadline,
                emptyCellLabel: t("dataTable.filter.notSpecified"),
            }),
        [isBeforeMentorSelectionDeadline, t]
    );

    const showCharts = breakdown && !breakdownFetching;
    const hasPrevSeason =
        showCharts &&
        (breakdown.byStudentStatus.some((b) => b.prevCount !== undefined) ||
            breakdown.byDiscipline.some((b) => b.prevCount !== undefined) ||
            breakdown.byRegion.some((b) => b.prevCount !== undefined));
    const footerSubText = showCharts
        ? hasPrevSeason
            ? t("mentees.charts.footerCompared")
            : t("mentees.charts.footerNoPrev")
        : undefined;

    return (
        <div className="container space-y-6 pb-12 pt-8">
            <div className="space-y-1">
                <PageTitle>{t("navbar.mentees")}</PageTitle>
                <p className="text-sm text-text-2">{t("mentees.pageDesc")}</p>
            </div>

            <div className="flex flex-col gap-6">
                {showCharts && (
                    <div className="order-2 grid gap-4 md:order-1 md:grid-cols-3">
                        <BreakdownBarChartCard
                            title={t("mentees.charts.studentStatus")}
                            description={t("mentees.charts.studentStatusDesc")}
                            data={breakdown.byStudentStatus}
                            totalCurrent={breakdown.totalCurrent}
                            totalPrev={breakdown.totalPrev}
                            footerText={t("mentees.charts.footer")}
                            footerSubText={footerSubText}
                            tooltipKeyPrefix="mentees.charts"
                        />
                        <BreakdownBarChartCard
                            title={t("mentees.charts.discipline")}
                            description={t("mentees.charts.disciplineDesc")}
                            data={breakdown.byDiscipline}
                            totalCurrent={breakdown.totalCurrent}
                            totalPrev={breakdown.totalPrev}
                            footerText={t("mentees.charts.footer")}
                            footerSubText={footerSubText}
                            tooltipKeyPrefix="mentees.charts"
                        />
                        <BreakdownBarChartCard
                            title={t("mentees.charts.region")}
                            description={t("mentees.charts.regionDesc")}
                            data={breakdown.byRegion}
                            totalCurrent={breakdown.totalCurrent}
                            totalPrev={breakdown.totalPrev}
                            footerText={t("mentees.charts.footer")}
                            footerSubText={footerSubText}
                            tooltipKeyPrefix="mentees.charts"
                        />
                    </div>
                )}

                <div className="order-1 md:order-2">
                    {isFetching && (
                        <div className="flex flex-col space-y-3">
                            <div className="space-y-2">
                                <Skeleton className="h-8 w-full" />
                                <Skeleton className="h-8 w-full" />
                                <Skeleton className="h-8 w-full" />
                                <Skeleton className="h-8 w-full" />
                                <Skeleton className="h-8 w-full" />
                                <Skeleton className="h-8 w-full" />
                            </div>
                        </div>
                    )}
                    {!isFetching && (
                        <DataTable
                            data={mentees}
                            columns={columns}
                            id="mentees"
                            hiddenColumns={hiddenColumns}
                            toolbarOptions={toolbarOptions}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default MenteesPage;
